import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from 'react';

export type PaletteCommand = {
  /** Unique identifier for the command */
  id: string;
  /** Display label for the command */
  label: string;
  /** Optional description shown below the label */
  description?: string;
  /** Optional icon component */
  icon?: React.ReactNode;
  /** The action to execute when the command is selected */
  action: () => Promise<void> | void;
  /** Optional group name for organizing commands */
  group?: string;
  /** Whether the command is enabled (can be a boolean or function) */
  enabled?: boolean | (() => boolean);
};

interface CommandPaletteContextValue {
  commands: PaletteCommand[];
  register: (cmds: PaletteCommand[]) => () => void; // returns unregister fn
}

const CommandPaletteContext = createContext<CommandPaletteContextValue | null>(
  null,
);

export const useCommandPaletteContext = () => {
  const ctx = useContext(CommandPaletteContext);
  if (!ctx) {
    throw new Error(
      'useCommandPaletteContext must be used within CommandPaletteProvider',
    );
  }
  return ctx;
};

export const CommandPaletteProvider: React.FC<React.PropsWithChildren> = ({
  children,
}) => {
  const [commands, setCommands] = useState<PaletteCommand[]>([]);

  const register = useCallback((cmds: PaletteCommand[]) => {
    setCommands((prev) => [...prev, ...cmds]);

    // Return an unregister function to clean up on unmount
    return () => {
      setCommands((prev) => prev.filter((c) => !cmds.includes(c)));
    };
  }, []);

  const contextValue = useMemo(
    () => ({ commands, register }),
    [commands, register],
  );

  return (
    <CommandPaletteContext.Provider value={contextValue}>
      {children}
    </CommandPaletteContext.Provider>
  );
};

// Convenience hook for components to register commands
export const useRegisterCommands = (
  cmds: PaletteCommand[] | (() => PaletteCommand[]),
) => {
  const { register } = useCommandPaletteContext();
  useEffect(() => {
    const cmdsArray = typeof cmds === 'function' ? cmds() : cmds;
    const unregister = register(cmdsArray);
    return unregister;
  }, [register, cmds]);
};
